// Voice mode: a hands-free conversation in the same chat (Kareem, 4 Nov: "what we say needs to show in the chat, just
// like when I speak with you"). One tap starts listening at once; your words stream into a live bubble as you speak;
// on a pause they are sent; the reply streams in as text and is read out sentence by sentence while it is still being
// written; then it listens again. Talk over it (or tap) to interrupt. Cards it proposes show in the chat with Approve,
// and saying "approve" approves the newest. End returns to the normal chat with everything kept.
//
// iOS: start() must be called inside the tap (it unlocks the one shared audio element and the AudioContext before
// anything is awaited); replies play on that element; a refused play shows "Tap to hear". The microphone stays open
// for the whole of voice mode (one permission prompt, a play-and-record session).
//
// Where live transcription cannot start, each turn is recorded and transcribed whole (the old way), with the same
// turn-taking on the microphone's level.
import { useEffect, useRef, useState } from 'react';
import { useMotionValue, type MotionValue } from 'motion/react';
import type { MessageResult } from '@dpw/shared';
import { api } from '../api';
import { LEVEL_START, LEVEL_STOP, fileFor, meterLevel, micError, recordingType } from '../lib/recorder';
import { playAudio, stopAudio, unlockAudio } from './audio';
import { canStream, openLive, tapPcm } from './live';

export type VoiceState = 'off' | 'starting' | 'listening' | 'hearing' | 'thinking' | 'speaking' | 'blocked';
export const VOICE_WORD: Record<VoiceState, string> = {
  off: 'Voice mode', starting: 'Starting', listening: 'Listening', hearing: 'Hearing you', thinking: 'Thinking', speaking: 'Speaking', blocked: 'Reply ready',
};

/** Talking over the reply interrupts it: the level must stay above this for about a third of a second. */
const BARGE_LEVEL = 0.34, BARGE_FRAMES = 20;
/** Without live transcription: a second of quiet ends a turn. */
const QUIET_MS = 1100, MAX_TURN_MS = 45_000;

/** Whole sentences from the start of a text, and what is left (final: the rest is a sentence too). */
export function sentences(buf: string, final = false): { out: string[]; rest: string } {
  const out: string[] = [];
  // A sentence ends at . ! ? (or the Arabic question mark) before a space, or at a line break; not "Dr." or "e.g.".
  const re = /(?<!\b(?:Dr|Mr|Mrs|Ms|St|vs|e\.g|i\.e))[.!?؟](?=\s)|\n+/g;
  let m: RegExpExecArray | null, from = 0;
  while ((m = re.exec(buf))) {
    const s = buf.slice(from, m.index + m[0].length).trim();
    if (s) out.push(s);
    from = m.index + m[0].length;
  }
  let rest = buf.slice(from);
  if (final && rest.trim()) { out.push(rest.trim()); rest = ''; }
  return { out, rest };
}

type Say = (text: string, o: { spoken: true; onText: (d: string) => void }) => Promise<MessageResult | null>;
/**
 * Voice control's hold on a turn (voice/pipeline.ts). partial: the words so far, as they arrive. final: the turn's
 * words when it ends; handled means it was done here (nothing goes to the assistant), else the words to send.
 * Chat voice mode passes none: every turn goes to the assistant.
 */
export interface TurnHooks {
  partial?: (words: string) => void;
  final?: (words: string) => { handled: true } | { handled: false; words: string };
}
interface View { state: (s: VoiceState) => void; heard: (t: string | null) => void; error: (m: string) => void; level: (v: number) => void }

/** One voice mode, from start to End. Plain state in one object, so the audio callbacks always see the latest. */
class VoiceSession {
  state: VoiceState = 'off';
  private gen = 0;
  private turn = 0;
  private stream: MediaStream | null = null;
  private stopMeter: (() => void) | null = null;
  private stopTap: (() => void) | null = null;
  private link: ReturnType<typeof openLive> | null = null;
  private live = true;
  private loud = 0;
  private rec: MediaRecorder | null = null;
  private quietSince = 0;
  private recStart = 0;
  private queue: string[] = [];
  private playing = false;
  /** The live turn: callbacks of an earlier turn's socket are ignored. */
  private tok = 0;
  hooks: TurnHooks | null = null;
  private replyDone = false;
  /** A sentence the browser would not play without a tap: its audio, and what was still to be read after it. */
  private held: { text: string; url: string | null; rest: string[] } | null = null;
  /** The whole of the rest of the reply as one read-out, for a browser that refuses every play without a tap. */
  private heldAll: string | null = null;

  constructor(private readonly v: View, public say: Say) {}

  private set(s: VoiceState) { this.state = s; this.v.state(s); }

  /** Call in the tap (iOS: before anything is awaited). */
  start() {
    if (this.state !== 'off') return;
    unlockAudio();
    this.stopAll();
    const gen = this.gen;
    this.v.error('');
    this.set('starting');
    void (async () => {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch (e) { this.set('off'); this.v.error(micError(e)); return; }
      if (gen !== this.gen) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null; return; }
      this.live = canStream();
      if (this.live) {
        try { this.stopTap = await tapPcm(this.stream, b => { if (this.state === 'listening' || this.state === 'hearing') this.link?.send(b); }); }
        catch { this.live = false; }
      }
      if (!this.stream) return;
      this.stopMeter = meterLevel(this.stream, (lv, running) => this.onLevel(lv, running, gen));
      this.listen(gen);
    })();
  }

  private onLevel(lv: number, running: boolean, gen: number) {
    this.v.level(lv);
    if (!running || gen !== this.gen) return;
    // Talking over the reply interrupts it.
    if (this.state === 'speaking') {
      this.loud = lv > BARGE_LEVEL ? this.loud + 1 : 0;
      if (this.loud >= BARGE_FRAMES) { this.loud = 0; this.interrupt(); }
      return;
    }
    if (this.live || (this.state !== 'listening' && this.state !== 'hearing')) return;
    // Without live words: speech starts a recording, a second of quiet ends it.
    const now = performance.now();
    if (!this.rec) { this.loud = lv > LEVEL_START ? this.loud + 1 : 0; if (this.loud >= 3) this.recordTurn(gen); return; }
    if (lv < LEVEL_STOP) { if (!this.quietSince) this.quietSince = now; } else this.quietSince = 0;
    if ((this.quietSince && now - this.quietSince > QUIET_MS) || now - this.recStart > MAX_TURN_MS) this.rec.stop();
  }

  /** Listen for one turn: live words into the bubble; the final words go to the assistant. keepError: a "Didn't catch
   * that" stays on the bar until you speak again. */
  private listen(gen: number, keepError = false) {
    if (gen !== this.gen || !this.stream) return;
    this.v.heard('');
    if (!keepError) this.v.error('');
    this.set('listening');
    this.quietSince = 0; this.loud = 0;
    if (!this.live) return;
    const finals: string[] = [];
    let partial = '';
    const tok = ++this.tok;
    const stale = () => gen !== this.gen || tok !== this.tok;
    this.link = openLive({ mode: 'talk', turn: this.turn++ }, {
      onPartial: t => {
        if (stale()) return;
        partial = t;
        if (t && this.state === 'listening') { this.v.error(''); this.set('hearing'); }
        const words = [...finals, partial].filter(Boolean).join(' ');
        this.v.heard(words);
        if (words) this.hooks?.partial?.(words);
      },
      onFinal: t => { if (stale()) return; finals.push(t); void this.think(finals.join(' '), gen); },
      onError: (m, started) => {
        if (stale()) return;
        this.link = null;
        // Live words could not start: record each turn the old way from now on.
        if (!started) { this.live = false; return; }
        this.v.error(m);
        this.listen(gen, true);
      },
    });
  }

  private recordTurn(gen: number) {
    if (!this.stream || this.rec) return;
    const mime = recordingType();
    const rec = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      this.rec = null;
      const blob = new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' });
      if (gen !== this.gen || !blob.size) return;
      this.set('thinking');
      try {
        const t = await api.transcribe(blob, fileFor(blob, 'turn'));
        if (gen === this.gen) void this.think(t.text, gen);
      } catch (e) { if (gen === this.gen) { this.v.error(e instanceof Error ? e.message : "Didn't catch that. Try again."); this.listen(gen, true); } }
    };
    rec.start();
    this.rec = rec; this.recStart = performance.now(); this.quietSince = 0;
    this.v.error('');
    this.set('hearing');
  }

  /**
   * Voice control did this turn itself, mid-sentence (a command matched before the pause): drop the rest of the turn
   * and listen for the next one. Never "Thinking".
   */
  handled() {
    if (this.state !== 'listening' && this.state !== 'hearing') return;
    this.tok++;
    this.link?.close(); this.link = null;
    this.v.heard(null);
    this.listen(this.gen);
  }

  /** Send the words; read the reply out sentence by sentence as it streams in. */
  private async think(words: string, gen: number) {
    if (gen !== this.gen) return;
    this.tok++;
    this.link?.close(); this.link = null;
    // Voice control's matcher first: a command is done here, at once, and never shows Thinking.
    const h = this.hooks?.final?.(words);
    if (h?.handled) { this.v.heard(null); this.listen(gen); return; }
    if (h) words = h.words;
    this.v.heard(null);
    this.set('thinking');
    this.replyDone = false; this.queue = [];
    let buf = '', any = false;
    const res = await this.say(words, {
      spoken: true,
      onText: d => {
        if (gen !== this.gen) return;
        buf += d;
        const s = sentences(buf);
        buf = s.rest;
        if (s.out.length) { any = true; this.queue.push(...s.out); void this.pump(gen); }
      },
    });
    if (gen !== this.gen) return;
    this.replyDone = true;
    if (!res) { this.v.error('That did not go through. Say it again.'); this.listen(gen, true); return; }
    // What streamed is read out; the rest of it, or the whole reply when nothing streamed (an approval, "Done.").
    this.queue.push(...(any || buf.trim() ? sentences(buf, true).out : sentences(res.reply.content, true).out));
    if (this.held) void this.holdAll(gen);
    else if (this.queue.length) void this.pump(gen); else if (!this.playing) this.listen(gen);
  }

  /** Play the queued sentences in order (the next one is fetched while one plays); then listen again. */
  private async pump(gen: number) {
    if (this.playing || this.held || gen !== this.gen) return;
    this.playing = true;
    const fetchAudio = (text: string) => api.voice.speak(text).then(r => URL.createObjectURL(r.audio), () => null);
    let pre: { text: string; url: Promise<string | null> } | null = null;
    const drop = (p: typeof pre) => { void p?.url.then(u => { if (u) URL.revokeObjectURL(u); }); };
    while ((pre || this.queue.length) && gen === this.gen) {
      const cur: { text: string; url: Promise<string | null> } = pre ?? (() => { const t = this.queue.shift()!; return { text: t, url: fetchAudio(t) }; })();
      pre = null;
      const url = await cur.url;
      if (this.queue.length) { const t = this.queue.shift()!; pre = { text: t, url: fetchAudio(t) }; }
      if (gen !== this.gen) { if (url) URL.revokeObjectURL(url); drop(pre); return; }
      if (!url) continue;
      if (this.state !== 'speaking') this.set('speaking');
      const res = await playAudio(url);
      if (res === 'refused') {
        // Not allowed to play without a tap: keep this sentence (its audio too) and the rest for "Tap to hear".
        if (pre) { this.queue.unshift(pre.text); drop(pre); }
        this.held = { text: cur.text, url, rest: this.queue.splice(0) };
        this.playing = false;
        if (this.replyDone) void this.holdAll(gen);
        return;
      }
      URL.revokeObjectURL(url);
      if (res === 'stopped') { drop(pre); this.playing = false; return; }
    }
    this.playing = false;
    if (gen === this.gen && this.replyDone && !this.queue.length) this.listen(gen);
  }

  /**
   * The reply is all in and a sentence was refused: make the rest of it one read-out, so one tap plays all of it
   * (a browser that refuses every play without a tap would otherwise want a tap per sentence). Then "Tap to hear".
   */
  private async holdAll(gen: number) {
    const h = this.held;
    if (!h || this.heldAll || this.state === 'blocked') return;
    const all = [h.text, ...h.rest, ...this.queue.splice(0)];
    h.rest = all.slice(1);
    if (all.length > 1) {
      const url = await api.voice.speak(all.join(' ')).then(r => URL.createObjectURL(r.audio), () => null);
      if (gen !== this.gen || this.held !== h) { if (url) URL.revokeObjectURL(url); return; }
      this.heldAll = url;
    }
    this.set('blocked');
  }

  private dropHeld() {
    if (this.held?.url) URL.revokeObjectURL(this.held.url);
    if (this.heldAll) URL.revokeObjectURL(this.heldAll);
    this.held = null; this.heldAll = null;
  }

  interrupt() {
    if (this.state !== 'speaking' && this.state !== 'blocked') return;
    this.gen++;
    this.queue = []; this.playing = false;
    this.dropHeld();
    stopAudio();
    this.listen(this.gen);
  }

  /** The browser refused to play without a tap; this is the tap: the held audio plays in it (no await before play). */
  tapToHear() {
    const h = this.held;
    if (this.state !== 'blocked' || !h) return;
    const gen = this.gen;
    const whole = this.heldAll;
    const url = whole ?? h.url;
    if (whole && h.url) URL.revokeObjectURL(h.url);
    this.held = null; this.heldAll = null;
    if (!whole) this.queue.unshift(...h.rest);
    if (!url) { unlockAudio(); this.queue.unshift(h.text); this.set('speaking'); void this.pump(gen); return; }
    this.set('speaking');
    this.playing = true;
    void playAudio(url).then(res => {
      URL.revokeObjectURL(url);
      this.playing = false;
      if (gen !== this.gen || res === 'stopped') return;
      if (res === 'refused') { this.held = { text: whole ? [h.text, ...h.rest].join(' ') : h.text, url: null, rest: [] }; this.set('blocked'); return; }
      if (this.queue.length) void this.pump(gen); else if (this.replyDone) this.listen(gen);
    });
  }

  /** Send what was heard so far now. */
  sendNow() { void this.link?.commit(); }

  private stopAll() {
    this.gen++;
    this.link?.close(); this.link = null;
    if (this.rec?.state === 'recording') { this.rec.onstop = null; this.rec.stop(); }
    this.rec = null;
    this.stopTap?.(); this.stopTap = null;
    this.stopMeter?.(); this.stopMeter = null;
    this.stream?.getTracks().forEach(t => t.stop()); this.stream = null;
    this.queue = []; this.playing = false;
    this.dropHeld();
    stopAudio();
    this.v.level(0);
  }

  end() { this.stopAll(); this.v.heard(null); this.set('off'); }
}

export function useVoiceMode(say: Say, hooks: TurnHooks | null = null) {
  const [state, setState] = useState<VoiceState>('off');
  const [heard, setHeard] = useState<string | null>(null);
  const [error, setError] = useState('');
  const level = useMotionValue(0);
  const s = useRef<VoiceSession | null>(null);
  if (!s.current) s.current = new VoiceSession({ state: setState, heard: setHeard, error: setError, level: v => level.set(v) }, say);
  s.current.say = say;
  s.current.hooks = hooks;
  const v = s.current;
  useEffect(() => () => v.end(), [v]);
  return {
    state, on: state !== 'off', heard, error, level: level as MotionValue<number>,
    start: () => v.start(), end: () => v.end(), interrupt: () => v.interrupt(), tapToHear: () => v.tapToHear(), sendNow: () => v.sendNow(), handled: () => v.handled(),
  };
}
